import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { forkJoin } from 'rxjs';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import {
  AddRolesDialogComponent,
  AddRolesDialogData,
  AddRolesDialogResult,
} from './components/add-roles-dialog/add-roles-dialog.component';
import { JobPosition } from './models/job-role-mapping.model';
import { JobRoleMappingService } from './services/job-role-mapping.service';

interface MappedRoleRow {
  name: string;
}

@Component({
  selector: 'app-job-role-mapping',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    ConfirmDialogModule,
    InputTextModule,
    AppButtonComponent,
    DataTableComponent,
  ],
  providers: [MessageService, ConfirmationService, DialogService],
  templateUrl: './job-role-mapping.component.html',
  styleUrls: ['./job-role-mapping.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class JobRoleMappingComponent implements OnInit {
  private readonly jobRoleMappingService = inject(JobRoleMappingService);
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);

  loadingJobs = signal(false);
  saving = signal(false);
  searchTerm = signal('');
  loadError = signal(false);

  private allJobs: JobPosition[] = [];
  jobs = signal<JobPosition[]>([]);
  selectedJob = signal<JobPosition | null>(null);

  /** Odoo job id -> mapped Keycloak role names. */
  private mappingsByJob = new Map<number, string[]>();

  mappedRoleRows = signal<MappedRoleRow[]>([]);
  rolesToRemove = signal<MappedRoleRow[]>([]);

  private dialogRef: DynamicDialogRef | null = null;

  readonly mappedRoleColumns: TableColumn[] = [
    {
      label: 'configuration.job_role_mapping.detail.table.role_code',
      prop: 'name',
      type: 'text',
      align: 'left',
      sortable: false,
    },
  ];

  hasSelectedJob = computed(() => this.selectedJob() !== null);

  ngOnInit(): void {
    this.loadData();
  }

  /** Loads job positions and existing mappings together, then builds the count map. */
  loadData(): void {
    this.loadingJobs.set(true);
    this.loadError.set(false);
    forkJoin({
      jobs: this.jobRoleMappingService.searchJobPositions(0, 1000),
      mappings: this.jobRoleMappingService.listMappings(0, 1000),
    }).subscribe({
      next: ({ jobs, mappings }) => {
        this.allJobs = jobs.pageContent || [];
        this.mappingsByJob = new Map(
          (mappings.pageContent || [])
            .filter((m) => m.odooJobId != null)
            .map((m) => [m.odooJobId, m.roleNames || []]),
        );
        this.applyFilter();
        this.restoreSelection();
        this.loadingJobs.set(false);
      },
      error: () => {
        this.allJobs = [];
        this.mappingsByJob = new Map();
        this.jobs.set([]);
        this.loadingJobs.set(false);
        this.loadError.set(true);
        this.showError('configuration.job_role_mapping.messages.load_failed');
      },
    });
  }

  onSearch(value: string): void {
    this.searchTerm.set(value);
    this.applyFilter();
  }

  private applyFilter(): void {
    const term = this.searchTerm().toLowerCase().trim();
    const filtered = term
      ? this.allJobs.filter((job) => (job.name || '').toLowerCase().includes(term))
      : [...this.allJobs];
    this.jobs.set(filtered);
  }

  /** Keeps the current job selected (and its role panel fresh) after a reload. */
  private restoreSelection(): void {
    const current = this.selectedJob();
    if (!current) {
      return;
    }
    const stillPresent = this.allJobs.find((job) => job.id === current.id);
    if (stillPresent) {
      this.selectedJob.set(stillPresent);
      this.refreshMappedRoles();
    } else {
      this.selectedJob.set(null);
      this.mappedRoleRows.set([]);
      this.rolesToRemove.set([]);
    }
  }

  roleCount(job: JobPosition): number {
    return this.mappingsByJob.get(job.id)?.length ?? 0;
  }

  onJobSelect(job: JobPosition | JobPosition[] | undefined): void {
    const selected = Array.isArray(job) ? job[0] : job;
    if (!selected) {
      return;
    }
    this.selectedJob.set(selected);
    this.refreshMappedRoles();
  }

  private currentRoleNames(): string[] {
    const job = this.selectedJob();
    if (!job) {
      return [];
    }
    return [...(this.mappingsByJob.get(job.id) ?? [])];
  }

  private refreshMappedRoles(): void {
    const rows = this.currentRoleNames()
      .slice()
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ name }));
    this.mappedRoleRows.set(rows);
    this.rolesToRemove.set([]);
  }

  openAddRolesDialog(): void {
    const job = this.selectedJob();
    if (!job) {
      return;
    }
    const data: AddRolesDialogData = {
      odooJobId: job.id,
      jobTitle: job.name,
      currentRoleNames: this.currentRoleNames(),
    };
    this.dialogRef = this.dialogService.open(AddRolesDialogComponent, {
      header: this.translate.instant(
        'configuration.job_role_mapping.dialog.add_roles_title',
        { job: job.name },
      ),
      width: '640px',
      modal: true,
      dismissableMask: true,
      data,
    });

    this.dialogRef.onClose.subscribe((result?: AddRolesDialogResult) => {
      this.dialogRef = null;
      if (result?.added?.length) {
        const merged = Array.from(
          new Set([...this.currentRoleNames(), ...result.added]),
        );
        this.persistRoles(job, merged, 'add_roles_success');
      }
    });
  }

  onRemoveSelectionChange(selection: MappedRoleRow[]): void {
    this.rolesToRemove.set(selection || []);
  }

  removeSelectedRoles(): void {
    const job = this.selectedJob();
    const toRemove = this.rolesToRemove();
    if (!job || toRemove.length === 0) {
      return;
    }
    const removeSet = new Set(toRemove.map((r) => r.name));
    const remaining = this.currentRoleNames().filter(
      (name) => !removeSet.has(name),
    );

    this.confirmationService.confirm({
      header: this.translate.instant(
        'configuration.job_role_mapping.detail.remove_confirm_title',
      ),
      message: this.translate.instant(
        'configuration.job_role_mapping.detail.remove_confirm',
        { count: toRemove.length },
      ),
      acceptLabel: this.translate.instant('common.actions.confirm'),
      rejectLabel: this.translate.instant('common.actions.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.persistRoles(job, remaining, 'remove_roles_success');
      },
    });
  }

  private persistRoles(
    job: JobPosition,
    roleNames: string[],
    successKey: string,
  ): void {
    this.saving.set(true);
    this.jobRoleMappingService.saveRoles(job.id, job.name, roleNames).subscribe({
      next: () => {
        if (roleNames.length === 0) {
          this.mappingsByJob.delete(job.id);
        } else {
          this.mappingsByJob.set(job.id, roleNames);
        }
        this.refreshMappedRoles();
        this.saving.set(false);
        this.showSuccess(`configuration.job_role_mapping.messages.${successKey}`);
      },
      error: () => {
        this.saving.set(false);
        this.showError('configuration.job_role_mapping.messages.save_failed');
      },
    });
  }

  private showSuccess(detailKey: string): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('common.messages.success'),
      detail: this.translate.instant(detailKey),
    });
  }

  private showError(detailKey: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('common.messages.error'),
      detail: this.translate.instant(detailKey),
    });
  }
}
